import React, { useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Rating,
  Avatar
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const AcademicDashboard = () => {
  const [selectedClass, setSelectedClass] = useState('all');
  const [selectedTerm, setSelectedTerm] = useState('term1');
  const [uploadData, setUploadData] = useState({
    class: '',
    subject: '',
    examType: '',
    date: '',
    file: null
  });

  // Sample performance data
  const performanceData = [
    { month: 'Jan', average: 75 },
    { month: 'Feb', average: 78 },
    { month: 'Mar', average: 80 },
    { month: 'Apr', average: 82 },
    { month: 'May', average: 85 },
  ];

  // Sample top teachers data
  const topTeachers = [
    { id: 1, name: 'John Doe', subject: 'Mathematics', rating: 4.8, performance: 92, avatar: '👨‍🏫' },
    { id: 2, name: 'Jane Smith', subject: 'English', rating: 4.7, performance: 90, avatar: '👩‍🏫' },
    { id: 3, name: 'Robert Johnson', subject: 'Science', rating: 4.6, performance: 88, avatar: '👨‍🏫' },
  ];

  // Sample class performance data
  const classPerformance = [
    { class: 'P.1', average: 82 },
    { class: 'P.2', average: 78 },
    { class: 'P.3', average: 85 },
    { class: 'P.4', average: 80 },
    { class: 'P.5', average: 76 },
    { class: 'P.6', average: 83 },
    { class: 'P.7', average: 79 },
  ];

  const classes = [
    'Nursery Top Class',
    'Nursery Middle Class',
    'Primary 1',
    'Primary 2',
    'Primary 3',
    'Primary 4',
    'Primary 5',
    'Primary 6',
    'Primary 7'
  ];

  const subjects = [
    'Mathematics',
    'English',
    'Science',
    'Social Studies',
    'Religious Education',
    'Physical Education',
    'Art and Craft'
  ];

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    setUploadData({ ...uploadData, file });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Submitting data:', uploadData);
    // Here you would typically handle the file upload to your backend
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ color: 'primary.dark' }}>
        Academic Performance Dashboard
      </Typography>

      {/* Performance Upload Form */}
      <Paper sx={{ p: 3, mb: 4, bgcolor: 'secondary.light' }}>
        <Typography variant="h6" gutterBottom>
          Upload Performance Data
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Class</InputLabel>
                <Select
                  value={uploadData.class}
                  onChange={(e) => setUploadData({ ...uploadData, class: e.target.value })}
                  label="Class"
                >
                  {classes.map((cls) => (
                    <MenuItem key={cls} value={cls}>{cls}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={uploadData.subject}
                  onChange={(e) => setUploadData({ ...uploadData, subject: e.target.value })}
                  label="Subject"
                >
                  {subjects.map((subject) => (
                    <MenuItem key={subject} value={subject}>{subject}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Exam Type</InputLabel>
                <Select
                  value={uploadData.examType}
                  onChange={(e) => setUploadData({ ...uploadData, examType: e.target.value })}
                  label="Exam Type"
                >
                  <MenuItem value="mid-term">Mid Term</MenuItem>
                  <MenuItem value="end-term">End of Term</MenuItem>
                  <MenuItem value="mock">Mock Exam</MenuItem>
                  <MenuItem value="final">Final Exam</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Exam Date"
                value={uploadData.date}
                onChange={(e) => setUploadData({ ...uploadData, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="file"
                InputLabelProps={{ shrink: true }}
                onChange={handleFileUpload}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                sx={{ mt: 2 }}
              >
                Upload Results
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Top Performing Teachers */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Top Performing Teachers
      </Typography>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {topTeachers.map((teacher, index) => (
          <Grid item xs={12} md={4} key={teacher.id}>
            <Card sx={{ bgcolor: index === 0 ? 'primary.light' : 'background.paper' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h1" sx={{ mr: 2 }}>{teacher.avatar}</Typography>
                  <Box>
                    <Typography variant="h6">{teacher.name}</Typography>
                    <Typography color="textSecondary">{teacher.subject}</Typography>
                  </Box>
                  {index === 0 && <EmojiEventsIcon sx={{ ml: 'auto', color: 'primary.main' }} />}
                </Box>
                <Rating value={teacher.rating} precision={0.1} readOnly />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Performance Score: {teacher.performance}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Performance Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 360 }}>
            <Typography variant="h6" gutterBottom>
              Monthly Performance Trend
            </Typography>
            <ResponsiveContainer>
              <LineChart data={performanceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="average" stroke="#87CEEB" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 360 }}>
            <Typography variant="h6" gutterBottom>
              Class Performance
            </Typography>
            <ResponsiveContainer>
              <BarChart data={classPerformance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="class" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="average" fill="#87CEEB" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AcademicDashboard;
